import type { ActionFunctionArgs, LoaderFunctionArgs } from "@remix-run/node";
import { defer, json, useActionData, useLoaderData } from "@remix-run/react";
import { cloudflareTurnstile, ROUTES, WALKTHROUGH } from "~/lib/constants";
import { Authenticator } from "~/services/auth.server";
import { z } from "zod";

import { Link } from "@remix-run/react";
import { Form } from "~/components/elements/Form";
import {
  formDataToObject,
  isResponseBodyError,
  jsonError,
  throwError,
  turnstileVerify,
} from "~/lib/utils";
import { components } from "schema";
import { ResponseBodyError } from "~/lib/types/ResponseError";
import { SectionWalkthrough } from "~/components/sections/SectionWalkthrough";
import { SetStateAction, useState } from "react";
import { TurnstileServerValidationResponse } from "@marsidev/react-turnstile";
import { WrapperFormRoot } from "~/components/wrappers/WrapperFormRoot";

const createForm = ({
  schools,
}: {
  schools: components["schemas"]["SchoolResource"][];
}) => {
  const form = {
    schema: [
      {
        type: "object",
        properties: {
          firstName: {
            type: "string",
            minLength: 1,
            errorMessage: {
              minLength: "Required",
            },
          },
          lastName: {
            type: "string",
            minLength: 1,
            errorMessage: {
              minLength: "Required",
            },
          },
          email: {
            type: "string",
            format: "email",
            minLength: 1,
            errorMessage: {
              format: "Invalid",
              minLength: "Required",
            },
          },
        },
        required: ["firstName", "lastName", "email"],
      },
      {
        type: "object",
        properties: {
          userType: {
            type: "string",
            minLength: 1,
            errorMessage: {
              minLength: "Required",
            },
          },
          schoolId: {
            type: "string",
            // minLength: 1,
            // errorMessage: {
            //   minLength: "Required",
            // },
          },
        },
        required: [
          "userType",
          // , "schoolId"
        ],
        // @link https://github.com/ajv-validator/ajv/issues/913#issuecomment-804508260
        dependencies: {
          userType: {
            if: { properties: { userType: { const: "teacher" } } },
            then: {
              properties: {
                schoolId: {
                  minLength: 1,
                  errorMessage: {
                    minLength: "Required",
                  },
                },
              },
            },
          },
        },
      },
      {
        type: "object",
        properties: {
          password: {
            type: "string",
            format: "strong-password",
            minLength: 1,
            errorMessage: {
              minLength: "Required",
            },
          },
          passwordConfirm: {
            type: "string",
            minLength: 1,
            const: { $data: "1/password" },
            errorMessage: {
              const: "Invalid",
              minLength: "Required",
            },
          },
        },
        required: ["password", "passwordConfirm"],
      },
    ],
    title: "Create your educator account",
    subtitle: {
      text: "Already have an account?",
      link: {
        to: "/login",
        text: "Log in",
      },
    },
    disclaimer: {
      text: "By clicking the Sign up button you acknowledge you have read and understood the <a href='https://journi.au/terms-of-use' target='_blank'>Terms of Use</a> and <a href='https://www.tmr.qld.gov.au/help/privacy' target='_blank'>Privacy Policy</a> and agree to be bound by them when interacting with this Site.",
    },
    inputs: [
      [
        {
          label: "First name",
          name: "firstName",
        },
        {
          label: "Last name",
          name: "lastName",
        },
        {
          label: "Email Address",
          name: "email",
          type: "email",
        },
      ],
      [
        {
          label: "I am",
          name: "userType",
          type: "select",
          options: [
            {
              label: "An Educator",
              value: "teacher",
            },
            {
              label: "A Community Group",
              value: "community_group",
            },
            {
              label: "A Parent",
              value: "parent",
            },
          ],
        },
        {
          label: "School",
          name: "schoolId",
          type: "select",
          parent: {
            name: "userType",
            value: "teacher",
          },
          options: schools.map((school) => {
            return {
              label: school.name,
              value: school.id,
            };
          }),
          // options: [
          //   {
          //     label: "Bedrock State Highschool",
          //     value: 1,
          //   },
          // ],
        },
      ],
      [
        {
          label: "Password",
          name: "password",
          type: "password",
        },
        {
          label: "Confirm Password",
          name: "passwordConfirm",
          type: "password",
        },
      ],
    ],
    submit: {
      label: "Sign up",
    },
    confirmation: {
      success: "Success",
      error: "Error",
    },
    background: {
      image: {
        src: "https://imagedelivery.net/5u-BnzY4d8R245ADRh1vnA/dfd77b6b-4d2e-47ea-3922-60824a447000/public",
      },
    },
  };

  return form;
};

export type TFormRegister = ReturnType<typeof createForm>;

export default function Route() {
  const { form, env } = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const responseError = actionData?.error?.message;
  const [walkthroughOpen, setWalkthroughOpen] = useState(true);
  return (
    <>
      {/* <SectionWalkthrough
            steps={WALKTHROUGH}
            defaultOpen={true}
            open={walkthroughOpen}
            setOpen={setWalkthroughOpen}
          /> */}
      <Form
        form={form as TFormRegister}
        responseError={responseError}
        turnstile={{ siteKey: env.CF_TURNSTILE_SITE_KEY }}
        walkthrough={true}
      />
    </>
  );
}

export async function action({ request, context }: ActionFunctionArgs) {
  const { env } = context.cloudflare;
  const authenticator = Authenticator({ context });

  const fields = await formDataToObject(request.clone());

  // Turnstile
  const turnstileVerification = await turnstileVerify({ request, fields, env });

  if (turnstileVerification) {
    return turnstileVerification;
  }

  // Educator

  let requestEducatorCreate = await fetch(
    `${env.BACKEND_BASE_URL}/api/educator/create`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(fields),
    }
  );

  let responseEducatorCreate:
    | components["schemas"]["CreateEducatorRequest"]
    | ResponseBodyError
    | undefined;
  try {
    responseEducatorCreate = (await requestEducatorCreate.json()) as
      | components["schemas"]["CreateEducatorRequest"]
      | ResponseBodyError;
  } catch (error) {
    return jsonError({
      response: requestEducatorCreate,
      message: "Failed to parse response JSON",
    });
    // console.log("Failed to parse response JSON");
  }

  if (!requestEducatorCreate.ok) {
    return jsonError({
      response: requestEducatorCreate,
      responseBody: isResponseBodyError(responseEducatorCreate)
        ? responseEducatorCreate
        : undefined,
    });
  }

  return await authenticator.authenticate("user-pass", request, {
    successRedirect: ROUTES.dashboard,
    failureRedirect: ROUTES.login,
  });
}

export async function loader({ request, context }: LoaderFunctionArgs) {
  const { env } = context.cloudflare;
  const authenticator = Authenticator({ context });

  await authenticator.isAuthenticated(request, {
    successRedirect: ROUTES.dashboard,
  });

  // Schools

  let requestSchools = await fetch(`${env.BACKEND_BASE_URL}/api/school/list`, {
    method: "GET",
  });

  let responseSchools;

  try {
    responseSchools =
      (await requestSchools.json()) as components["schemas"]["SchoolResource"][];
  } catch (error) {
    return throwError({
      response: requestSchools,
      message: "Failed to parse response JSON",
    });
  }

  if (!requestSchools.ok) {
    return throwError({
      response: requestSchools,
      responseBody: isResponseBodyError(responseSchools)
        ? responseSchools
        : undefined,
    });
  }

  const form = createForm({ schools: responseSchools });

  return defer({
    form,
    env: {
      CF_TURNSTILE_SITE_KEY: env.CF_TURNSTILE_SITE_KEY,
    },
  });
}
